<template>
  <div>
    <div class="d-flex flex-row mb-6 align-center">
      <p class="text-2xl mb-6">Liste trafic GEO</p>
      <v-spacer></v-spacer>
    </div>

    <!-- Stats Panel -->
    <v-expansion-panels v-model="statsPanelVisible" multiple class="mb-4">
      <v-expansion-panel>
        <v-expansion-panel-header>Statistiques Globales</v-expansion-panel-header>
        <v-expansion-panel-content>
          <traffic-stats
            :params="currentFilters"
            :is-visible="isStatsPanelOpen"
          ></traffic-stats>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-card>
      <v-card-text>
        <div class="d-flex flex-column mb-6">
          <v-row align="center">
            <v-col cols="2">
              <date-range-filter
                @valueChanged="setDates"
                periodType="past-and-today"
                format="date"
                :range="30"
                :initialValue="null"
                :onCreatedEmitDates="true"
                :frequencies="['daily']"
                :showBtnList="[]"
                :outlined="true"
                class="hide-frequency-toggle"
              ></date-range-filter>
            </v-col>

            <v-col cols="2">
              <v-select
                v-model="searchSource"
                :items="sources"
                :loading="sourcesLoading"
                label="Source"
                dense
                clearable
                @focus="fetchSources"
              ></v-select>
            </v-col>

            <v-col cols="2">
              <v-autocomplete
                v-model="searchSite"
                :items="activeSites"
                item-text="text"
                item-value="value"
                cache-items
                :loading="activeSitesLoading"
                label="Site"
                dense
                clearable
                @change="setSearchSite"
                @focus="fetchActiveSites"
              ></v-autocomplete>
            </v-col>

            <v-col cols="2">
              <v-select
                v-model="searchBotName"
                :items="botNames"
                :loading="botNamesLoading"
                label="Bot name"
                dense
                clearable
                @focus="fetchBotNames"
              ></v-select>
            </v-col>

            <v-col class="d-flex align-center">
              <span class="text-caption font-weight-bold mr-1">Détail par :</span>
              <v-chip-group v-model="activeGroupings" multiple active-class="primary--text" class="mt-0 pt-0">
                <v-chip filter value="date" x-small outlined class="ma-1">Date</v-chip>
                <v-chip filter value="source" x-small outlined class="ma-1">Source</v-chip>
                <v-chip filter value="bot_name" x-small outlined class="ma-1">Bot</v-chip>
              </v-chip-group>
            </v-col>

            <v-col cols="auto" class="d-flex justify-end align-center">
              <dot-menu
                @exportExcel="exportExcel"
                :excelButton="{
                  excelData: excelButton.data,
                  filename: 'geo-traffic-export.xlsx',
                  disabled: false,
                }"
              />
            </v-col>
          </v-row>

          <v-row align="center">
            <v-col cols="12">
              <v-text-field
                v-model.trim="urlContainsSearch"
                label="URL contains (quick search)"
                hint="Ex: dentifrice. While filled, this temporarily overrides Include paths below."
                persistent-hint
                dense
                prepend-inner-icon="mdi-magnify"
                outlined
                clearable
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Include filter -->
          <v-row align="center">
            <v-col cols="10">
              <v-combobox
                v-model="pathPatternsIncluded"
                chips
                clearable
                label="Include paths (wildcards allowed, e.g. */embed/*)"
                multiple
                dense
                prepend-inner-icon="mdi-filter-variant"
                outlined
                hide-selected
              >
                <template v-slot:selection="{ attrs, item, select, selected }">
                  <v-chip
                    v-bind="attrs"
                    :input-value="selected"
                    close
                    small
                    color="primary"
                    @click="select"
                    @click:close="removePattern(item, 'included')"
                  >
                    <strong>{{ item }}</strong>
                  </v-chip>
                </template>
              </v-combobox>
            </v-col>
            <v-col cols="2">
              <v-switch
                v-model="pathPatternsIncludedLogic"
                :label="`Logic: ${pathPatternsIncludedLogic.toUpperCase()}`"
                true-value="and"
                false-value="or"
                dense
                class="mt-0 pt-0"
                @change="saveSettings"
              ></v-switch>
            </v-col>
          </v-row>

          <v-row class="mt-1">
            <v-col cols="12" class="d-flex justify-end">
              <v-btn
                color="primary"
                :loading="loading"
                :disabled="loading"
                @click="applyFilters"
              >
                Appliquer les filtres
              </v-btn>
            </v-col>
          </v-row>
        </div>

        <v-row v-if="data.items.length > 0">
          <v-col cols="12">
            <v-data-table
              locale="fr-FR"
              :page="pagination.options.page"
              :page-count="pagesNumber"
              :headers="computedTableHeaders"
              :items="data.items"
              :sort-by="'date'"
              :sort-desc="true"
              :options.sync="pagination.options"
              :server-items-length="data.total"
              :loading="loading"
              class="elevation-1"
              style="width: 100%"
              :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
              :items-per-page="pagination.options.itemsPerPage"
              dense
            >
              <template v-slot:item.site_id="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on">
                      {{ getSiteLabel(item) }}
                    </div>
                  </template>
                  <span>ID: {{ item.site_id }}</span>
                </v-tooltip>
              </template>

              <template v-slot:item.date="{ item }">
                {{ formatDate(item.date) }}
              </template>

              <template v-slot:item.bot_name="{ item }">
                {{ item.bot_name || "-" }}
              </template>

              <template v-slot:item.path="{ item }">
                <v-tooltip top v-if="item.path">
                  <template v-slot:activator="{ on, attrs }">
                    <div
                      v-bind="attrs"
                      v-on="on"
                      class="text-truncate"
                      style="max-width: 800px"
                    >
                      <a
                        v-if="item.url"
                        :href="item.url"
                        target="_blank"
                        class="text-decoration-none"
                      >
                        {{ item.path }}
                      </a>
                      <span v-else>{{ item.path }}</span>
                    </div>
                  </template>
                  <span>{{ item.url }}</span>
                </v-tooltip>
                <span v-else>-</span>
              </template>

              <template v-slot:item.requests="{ item }">
                {{
                  item.requests === null || item.requests === undefined
                    ? "-"
                    : item.requests
                }}
              </template>
            </v-data-table>
          </v-col>
        </v-row>

        <v-row v-else>
          <div v-if="loading" style="width: 100%">
            <v-progress-linear indeterminate></v-progress-linear>
          </div>
          <div v-else class="pa-4">Pas de resultats</div>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Exclude filter card -->
    <v-card class="mt-6">
      <v-card-title class="text-h6">
        Ne pas afficher les urls contenant ces chaines de caractères
      </v-card-title>
      <v-card-text>
        <v-row align="center">
          <v-col cols="12">
            <v-combobox
              v-model="pathPatternsExcluded"
              chips
              clearable
              label="Exclude paths (AND NOT)"
              multiple
              dense
              prepend-inner-icon="mdi-filter-variant-remove"
              outlined
              hide-selected
            >
              <template v-slot:selection="{ attrs, item, select, selected }">
                <v-chip
                  v-bind="attrs"
                  :input-value="selected"
                  close
                  small
                  color="error"
                  @click="select"
                  @click:close="removePattern(item, 'excluded')"
                >
                  <strong>{{ item }}</strong>
                </v-chip>
              </template>
            </v-combobox>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <rmr-confirm-dialog
      v-model="slowQueryDialogVisible"
      title="Requête potentiellement lente"
      message="Aucun site n'est sélectionné. La requête peut être lente sur un grand volume de données. Voulez-vous continuer ?"
      confirm-label="Continuer"
      cancel-label="Annuler"
      @confirm="resolveSlowQueryDialog(true)"
      @cancel="resolveSlowQueryDialog(false)"
    />
  </div>
</template>

<script>
import store from "@/store";
import axios from "@axios";
import {
  computed,
  getCurrentInstance,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from "@vue/composition-api";

import DotMenu from "@/components/common/menus/DotMenu";
import DateRangeFilter from "@/components/common/filters/DateRangeFilter.vue";
import TrafficStats from "@/components/geo/TrafficStats.vue";
import RmrConfirmDialog from "@/components/ui/actions/RmrConfirmDialog.vue";
import { NOTIF_DISPLAY_DURATION } from "@/utils/constants";
import { getDateStr, parseDateAsUtc } from "@/utils/dates";
import { saveRmraView } from "@/utils/rmr-analytics";

const formatDate = (val) => {
  if (!val) return "-";
  const d = parseDateAsUtc(val);
  if (isNaN(d.getTime())) return val;
  return getDateStr(d);
};

const DEFAULT_GEO_TRAFFIC_SITE_KEYWORD = "marmiton";

export default {
  name: "GeoTrafficList",
  components: {
    DateRangeFilter,
    DotMenu,
    RmrConfirmDialog,
    TrafficStats,
  },
  setup() {
    const instance = getCurrentInstance();
    const route = instance?.proxy?.$route;

    const excelButton = reactive({
      data: new Blob([""], { type: "text/plain" }),
    });

    const data = reactive({ total: 0, items: [] });
    const pagination = reactive({
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ["date"],
        sortDesc: [true],
      },
    });

    const pagesNumber = computed(() => {
      if (!data.total) return 0;
      return Math.ceil(data.total / pagination.options.itemsPerPage);
    });

    const loading = ref(false);
    const pendingFetch = ref(false);
    const optionsUpdatedByHuman = ref(false);
    const isFetchingSettings = ref(false);
    const inferredInitialSite = ref(false);
    const slowQueryDialogVisible = ref(false);
    const slowQueryDialogResolver = ref(null);
    // Stats panel state
    const statsPanelVisible = ref([]); 
    const isStatsPanelOpen = computed(() => statsPanelVisible.value.includes(0));

    const initialSiteId = route?.query?.site_id
      ? parseInt(route.query.site_id, 10)
      : null;
    const appliedSiteId = ref(initialSiteId);
    const appliedSource = ref(null);
    const appliedBotName = ref(null);
    const appliedDates = ref(null);
    const searchSite = ref(initialSiteId);
    const searchSource = ref(null);
    const searchBotName = ref("");
    const urlContainsSearch = ref("");
    const appliedUrlContainsSearch = ref("");
    const pathPatternsIncluded = ref([]);
    const pathPatternsIncludedLogic = ref("or");
    const pathPatternsExcluded = ref([]);
    const appliedPathPatternsIncluded = ref([]);
    const appliedPathPatternsIncludedLogic = ref("or");
    const appliedPathPatternsExcluded = ref([]);
    const activeGroupings = ref(["date", "source", "bot_name"]);
    const dates = ref(null);

    const botNames = ref([]);
    const sources = ref([]);
    const activeSites = ref([]);
    const botNamesLoading = ref(false);
    const sourcesLoading = ref(false);
    const activeSitesLoading = ref(false);
    const botNamesLoaded = ref(false);
    const sourcesLoaded = ref(false);
    const activeSitesLoaded = ref(false);

    const fetchBotNames = async () => {
      if (botNamesLoading.value || botNamesLoaded.value) return;
      botNamesLoading.value = true;
      try {
        const response = await axios.get("/geo/traffic/bots");
        botNames.value = response.data;
        botNamesLoaded.value = true;
      } catch (error) {
        console.error("Error fetching bot names:", error);
      } finally {
        botNamesLoading.value = false;
      }
    };

    const fetchSources = async () => {
      if (sourcesLoading.value || sourcesLoaded.value) return;
      sourcesLoading.value = true;
      try {
        const response = await axios.get("/geo/traffic/sources");
        sources.value = response.data;
        sourcesLoaded.value = true;
      } catch (error) {
        console.error("Error fetching sources:", error);
      } finally {
        sourcesLoading.value = false;
      }
    };

    const fetchActiveSites = async () => {
      if (activeSitesLoading.value || activeSitesLoaded.value) return;
      activeSitesLoading.value = true;
      try {
        const response = await axios.get("/geo/traffic/sites");
        activeSites.value = response.data
          .map((site) => ({
            text: site.name || site.domain,
            value: site.id,
            name: site.name || "",
            domain: site.domain || "",
          }))
          .sort((a, b) => a.text.localeCompare(b.text));
        activeSitesLoaded.value = true;
      } catch (error) {
        console.error("Error fetching active sites:", error);
      } finally {
        activeSitesLoading.value = false;
      }
    };

    const fetchSettings = async (siteId = searchSite.value || null) => {
      isFetchingSettings.value = true;
      try {
        const params = { site_id: siteId };
        const response = await axios.get("/geo/traffic/settings", { params });
        pathPatternsIncluded.value = response.data.patterns || [];
        pathPatternsIncludedLogic.value = response.data.logic || "or";
        pathPatternsExcluded.value = response.data.excludes || [];
      } catch (error) {
        console.error("Error fetching settings:", error);
        pathPatternsIncluded.value = [];
        pathPatternsIncludedLogic.value = "or";
        pathPatternsExcluded.value = [];
      } finally {
        instance?.proxy?.$nextTick(() => {
          isFetchingSettings.value = false;
        });
      }
    };

    const saveSettings = async (siteId = searchSite.value || null) => {
      if (isFetchingSettings.value) return;
      try {
        const settings = {
          patterns: pathPatternsIncluded.value,
          logic: pathPatternsIncludedLogic.value,
          excludes: pathPatternsExcluded.value,
        };
        const params = { site_id: siteId };
        await axios.post("/geo/traffic/settings", settings, { params });
      } catch (error) {
        console.error("Error saving settings:", error);
      }
    };

    const removePattern = (item, type) => {
      if (type === "included") {
        const index = pathPatternsIncluded.value.indexOf(item);
        if (index >= 0) pathPatternsIncluded.value.splice(index, 1);
      } else {
        const index = pathPatternsExcluded.value.indexOf(item);
        if (index >= 0) pathPatternsExcluded.value.splice(index, 1);
      }
    };

    const siteNameById = computed(() => {
      const list = store.getters["common/getSiteList"] || [];
      return list.reduce((acc, site) => {
        acc[site.id] = site.name;
        return acc;
      }, {});
    });

    const normalizeSiteId = (value) => {
      const candidate =
        value && typeof value === "object"
          ? value.value ?? value.id ?? null
          : value;
      if (candidate === null || candidate === undefined || candidate === "") {
        return null;
      }
      const parsed = parseInt(candidate, 10);
      return Number.isNaN(parsed) ? null : parsed;
    };

    const findDefaultGeoTrafficSiteId = (sites = []) => {
      const defaultSite = sites.find((site) => {
        const haystack = [site?.text, site?.name, site?.domain]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return haystack.includes(DEFAULT_GEO_TRAFFIC_SITE_KEYWORD);
      });

      return normalizeSiteId(defaultSite?.value ?? defaultSite?.id ?? null);
    };

    const resetToFirstPage = () => {
      if (pagination.options.page !== 1) {
        optionsUpdatedByHuman.value = false;
        pagination.options.page = 1;
      }
    };

    const syncAppliedFilters = ({
      siteId = appliedSiteId.value,
      datesValue = appliedDates.value,
    } = {}) => {
      appliedSiteId.value = siteId;
      appliedSource.value = searchSource.value || null;
      appliedBotName.value = searchBotName.value || null;
      appliedUrlContainsSearch.value = String(urlContainsSearch.value || "").trim();
      appliedPathPatternsIncluded.value = [...pathPatternsIncluded.value];
      appliedPathPatternsIncludedLogic.value = pathPatternsIncludedLogic.value;
      appliedPathPatternsExcluded.value = [...pathPatternsExcluded.value];
      appliedDates.value = datesValue || null;
    };

    const resolveSlowQueryDialog = async (confirmed) => {
      slowQueryDialogVisible.value = false;
      if (slowQueryDialogResolver.value) {
        slowQueryDialogResolver.value(confirmed);
        slowQueryDialogResolver.value = null;
      }
      if (!confirmed) {
        await store.dispatch("app/setSnackBar", {
          show: true,
          text: "Recherche globale annulée.",
          color: "warning",
          timeout: NOTIF_DISPLAY_DURATION,
        });
      }
    };

    const confirmSlowGlobalQuery = () => {
      slowQueryDialogVisible.value = true;
      return new Promise((resolve) => {
        slowQueryDialogResolver.value = resolve;
      });
    };

    const syncSiteContext = async (
      siteId,
      { syncApplied = false } = {}
    ) => {
      searchSite.value = siteId;
      await fetchSettings(siteId);
      if (syncApplied) {
        syncAppliedFilters({
          siteId,
          datesValue: dates.value,
        });
      }
    };

    const quickPathPattern = computed(() => {
      const value = String(appliedUrlContainsSearch.value || "").trim();
      return value ? `*${value}*` : null;
    });

    const effectivePathPatternsIncluded = computed(() => {
      if (quickPathPattern.value) return [quickPathPattern.value];
      return appliedPathPatternsIncluded.value.length > 0
        ? appliedPathPatternsIncluded.value
        : null;
    });

    const currentFilters = computed(() => ({
      site_id: appliedSiteId.value || null,
      source: appliedSource.value || null,
      bot_name: appliedBotName.value || null,
      path_patterns_included: effectivePathPatternsIncluded.value,
      path_patterns_included_logic: quickPathPattern.value
        ? "or"
        : appliedPathPatternsIncludedLogic.value,
      path_patterns_excluded:
        appliedPathPatternsExcluded.value.length > 0
          ? appliedPathPatternsExcluded.value
          : null,
      dates: appliedDates.value || null,
    }));

    const setSearchSite = async (val) => {
      const nextSiteId = normalizeSiteId(val);
      const hasExplicitValue =
        val !== null && val !== undefined && String(val).trim() !== "";

      if (hasExplicitValue && nextSiteId === null) {
        searchSite.value = appliedSiteId.value;
        return;
      }

      await syncSiteContext(nextSiteId);
    };

    const setDates = (val) => {
      let serializedValue = null;

      if (Array.isArray(val) && val.length === 2 && val[0] && val[1]) {
        serializedValue = `${val[0]},${val[1]}`;
      } else if (typeof val === "string" && val.includes(",")) {
        const [from, to] = val.split(",").map((item) => item.trim());
        if (from && to) {
          serializedValue = `${from},${to}`;
        }
      }

      if (serializedValue === dates.value) {
        return;
      }

      dates.value = serializedValue;
    };

    const paramsSerializer = (params) => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          if (value.length === 0) {
            searchParams.append(key, "");
          } else {
            value.forEach((v) => searchParams.append(key, v));
          }
        } else if (value !== null) {
          searchParams.append(key, value);
        }
      });
      return searchParams.toString();
    };

    const exportExcel = async () => {
      const url = "/geo/traffic/export";
      const params = { 
        ...currentFilters.value,
        group_by: activeGroupings.value 
      };
      if (pagination.options.sortBy?.length > 0) {
        params.sort_by = pagination.options.sortBy[0];
        params.sort_desc = pagination.options.sortDesc[0];
      }
      try {
        await store.dispatch("app/setSnackBar", {
          show: true,
          text: "Export en cours...",
          color: "info",
          timeout: NOTIF_DISPLAY_DURATION,
        });
        const { data } = await axios.get(url, {
          params,
          paramsSerializer,
          responseType: "blob",
        });
        excelButton.data = data;
      } catch (error) {
        console.error(error);
        await store.dispatch("app/setSnackBar", {
          show: true,
          text: "Erreur export.",
          color: "error",
          timeout: NOTIF_DISPLAY_DURATION,
        });
      }
    };

    const applyFilters = async () => {
      const nextSiteId = normalizeSiteId(searchSite.value);

      if (nextSiteId === null && dates.value) {
        const confirmed = await confirmSlowGlobalQuery();
        if (!confirmed) {
          return;
        }
      }

      syncAppliedFilters({
        siteId: nextSiteId,
        datesValue: dates.value,
      });
      resetToFirstPage();
      await saveSettings(nextSiteId);
      fetchData();
    };

    const inferSiteIdFromItems = (items = []) => {
      const uniqueSiteIds = [
        ...new Set(
          items
            .map((item) => normalizeSiteId(item?.site_id))
            .filter((siteId) => siteId !== null)
        ),
      ];

      return uniqueSiteIds.length === 1 ? uniqueSiteIds[0] : null;
    };

    const fetchData = async () => {
      if (loading.value) {
        pendingFetch.value = true;
        return;
      }
      loading.value = true;
      pendingFetch.value = false;
      try {
        const params = {
          ...currentFilters.value,
          group_by: activeGroupings.value,
          page: pagination.options.page - 1,
          limit: pagination.options.itemsPerPage,
        };
        if (pagination.options.sortBy?.length > 0) {
          params.sort_by = pagination.options.sortBy[0];
          params.sort_desc = pagination.options.sortDesc[0];
        }
        const response = await axios.get("/geo/traffic", {
          params,
          paramsSerializer,
        });
        data.total = response.data.total;
        data.items = response.data.items;

        if (
          !inferredInitialSite.value &&
          initialSiteId === null &&
          appliedSiteId.value === null &&
          searchSite.value === null
        ) {
          const inferredSiteId = inferSiteIdFromItems(response.data.items);
          if (inferredSiteId !== null) {
            inferredInitialSite.value = true;
            await syncSiteContext(inferredSiteId, { syncApplied: true });
            pendingFetch.value = true;
          }
        }
      } catch (error) {
        console.error(error);
        await store.dispatch("app/setSnackBar", {
          show: true,
          text: "Erreur chargement.",
          color: "error",
          timeout: NOTIF_DISPLAY_DURATION,
        });
      } finally {
        loading.value = false;
        if (pendingFetch.value) {
          pendingFetch.value = false;
          fetchData();
        }
      }
    };

    const getSiteLabel = (item) => {
      if (item.site_name) return item.site_name;
      if (siteNameById.value[item.site_id])
        return siteNameById.value[item.site_id];
      return item.site_id ?? "-";
    };

    const computedTableHeaders = computed(() => {
      const headers = [];
      const optional_dims = ["date", "source", "bot_name"];
      const current_groups = activeGroupings.value;
      const is_raw = optional_dims.every((d) => current_groups.includes(d));

      if (is_raw) headers.push({ text: "ID", value: "id" });
      headers.push({ text: "Site", value: "site_id" });
      if (current_groups.includes("date"))
        headers.push({ text: "Date", value: "date" });
      if (current_groups.includes("source"))
        headers.push({ text: "Source", value: "source" });
      if (current_groups.includes("bot_name"))
        headers.push({ text: "Bot", value: "bot_name" });
      headers.push(
        { text: "Path", value: "path", width: "40%" },
        { text: "Requests", value: "requests" }
      );
      return headers;
    });

    onMounted(async () => {
      store.commit("appConfig/UPDATE_APP_CONTENT_WIDTH", "fullWidth");
      await fetchActiveSites();

      const resolvedInitialSiteId =
        initialSiteId !== null
          ? initialSiteId
          : findDefaultGeoTrafficSiteId(activeSites.value) ??
            (activeSites.value.length === 1
              ? normalizeSiteId(activeSites.value[0]?.value)
              : null);

      await syncSiteContext(resolvedInitialSiteId, { syncApplied: true });
      fetchData();
      if (route) saveRmraView(route);
    });

    onBeforeUnmount(() => {
      store.commit("appConfig/UPDATE_APP_CONTENT_WIDTH", "boxed");
    });

    watch(activeGroupings, fetchData);
    watch(
      () => pagination.options,
      () => {
        if (!optionsUpdatedByHuman.value) {
          optionsUpdatedByHuman.value = true;
          return;
        }
        fetchData();
      },
      { deep: true }
    );

    return {
      data,
      excelButton,
      pagination,
      pagesNumber,
      loading,
      botNamesLoading,
      sourcesLoading,
      activeSitesLoading,
      botNames,
      sources,
      activeSites,
      searchSource,
      searchBotName,
      urlContainsSearch,
      pathPatternsIncluded,
      pathPatternsIncludedLogic,
      pathPatternsExcluded,
      activeGroupings,
      searchSite,
      setSearchSite,
      setDates,
      applyFilters,
      getSiteLabel,
      formatDate,
      computedTableHeaders,
      exportExcel,
      fetchBotNames,
      fetchSources,
      fetchActiveSites,
      removePattern,
      saveSettings,
      statsPanelVisible,
      isStatsPanelOpen,
      currentFilters,
      slowQueryDialogVisible,
      resolveSlowQueryDialog,
    };
  },
};
</script>

<style scoped>
::v-deep .hide-frequency-toggle .v-btn-toggle-frequency {
  display: none !important;
}
</style>
